/**
 * The 100-level trail up an Antarctic mountain.
 *
 * Ten levels per screen-tall region; arrows and the wheel scroll it. A level is
 * locked until the one below it is complete (CLAUDE.md). The timed-mode toggle
 * the brief puts here is deliberately absent while that mechanic is undecided.
 */
import Phaser from 'phaser';
import { setDevState } from '../systems/devQuery';
import { ensurePlaceholderTexture } from '../art/placeholders';
import { isSentenceLevel, MAX_LEVEL } from '../systems/LevelGenerator';
import { AnimalSprite } from '../art/AnimalSprite';
import { animalById, bodyTintFor } from '../data/animals';
import { THEME_BANDS, WORLD_NAMES } from '../data/themes';
import { drawMapScenery, drawSouthPole } from './map/MapScenery';
import type { MapScenery } from './map/MapScenery';
import {
  allLevelNodes,
  MAP_HEADER_HEIGHT,
  mapBounds,
  scrollYForLevel,
  summitPoint,
} from '../systems/levelMapPath';
import type { MapNode } from '../systems/levelMapPath';
import type { SaveManager } from '../systems/SaveManager';
import { FONT_FAMILY, GAME_HEIGHT, GAME_WIDTH, IS_PORTRAIT, PALETTE } from '../types';
import { pressButton, styleButton } from './ui/menuButton';
import { TIDE_LOCKED_HINT, tideOpenFor } from './TideStart';

const NODE_RADIUS = 30;
const SCROLL_STEP = 120;
const HEADER_DEPTH = 100;

export class LevelMap extends Phaser.Scene {
  private save!: SaveManager;
  private nodes: MapNode[] = [];
  private starsText!: Phaser.GameObjects.Text;
  private current = 1;
  private highest = 1;
  /**
   * Where the arrow keys are pointing. The header buttons used to be mouse
   * only - every arrow moved between levels and Esc went to Players - so a
   * child on the keyboard could never reach Animals or Settings.
   */
  private zone: 'map' | 'header' = 'map';
  private headerFocus = 0;
  private headerButtons: Phaser.GameObjects.Text[] = [];
  private headerActions: Array<() => void> = [];
  private scenery: MapScenery | undefined;
  private focusRing: Phaser.GameObjects.Graphics | undefined;
  private dragged = false;
  private dragFromY = 0;
  private dragFromScroll = 0;

  constructor() {
    super('LevelMap');
  }

  create(): void {
    setDevState({ scene: 'LevelMap' });
    this.save = this.registry.get('save') as SaveManager;
    // Players from before the grade question and helpers page see them once.
    if (this.save.activeProfile()?.seenHelpers === false) {
      this.scene.start('Grade');
      return;
    }
    this.cameras.main.setBackgroundColor(PALETTE.sky);

    // Scene instances are reused, so nothing may carry over from a last visit.
    this.zone = 'map';
    this.dragged = false;
    this.headerFocus = 0;
    this.headerButtons = [];
    this.headerActions = [];

    this.nodes = allLevelNodes(MAX_LEVEL);
    this.highest = this.save.highestUnlockedLevel(MAX_LEVEL);
    this.current = this.highest;

    const bounds = mapBounds(MAX_LEVEL);
    this.cameras.main.setBounds(bounds.x, bounds.y, bounds.width, bounds.height);

    this.scenery = drawMapScenery(this, this.nodes);
    this.drawTrail();
    this.drawPole();
    this.drawSignposts();
    this.drawNodes();
    this.drawFocusRing();
    this.placeClimber();
    this.drawHeader();
    this.installInput();

    this.cameras.main.setScroll(0, scrollYForLevel(this.current, MAX_LEVEL));
  }

  /* --------------------------------------------------------------- drawing */

  private drawTrail(): void {
    // A smooth snow path through every node, rather than straight segments.
    const spline = new Phaser.Curves.Spline(
      this.nodes.map((n) => new Phaser.Math.Vector2(n.x, n.y)),
    );
    const perSegment = 16;
    const points = spline.getPoints(this.nodes.length * perSegment);
    // Spline t is spread evenly over control points, so this is the open part.
    const openEnd = Math.min(points.length, (this.highest - 1) * perSegment + 1);

    const stroke = (
      from: number,
      to: number,
      width: number,
      color: number,
      alpha: number,
    ): void => {
      if (to - from < 2) return;
      const g = this.add.graphics().setDepth(1);
      g.lineStyle(width, color, alpha);
      g.beginPath();
      for (let i = from; i < to; i += 1) {
        const p = points[i];
        if (p === undefined) continue;
        if (i === from) g.moveTo(p.x, p.y);
        else g.lineTo(p.x, p.y);
      }
      g.strokePath();
    };
    stroke(0, points.length, 26, 0x0b1220, 0.28);
    stroke(0, openEnd, 16, 0xf4f9ff, 0.95);
    stroke(Math.max(0, openEnd - 1), points.length, 16, 0xf4f9ff, 0.38);

    // Footprints up the part already walked.
    const prints = this.add.graphics().setDepth(1);
    prints.fillStyle(0x8fb4d1, 0.9);
    for (let i = 2; i < openEnd - 1; i += 3) {
      const a = points[i - 1];
      const b = points[i + 1];
      const p = points[i];
      if (a === undefined || b === undefined || p === undefined) continue;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const len = Math.hypot(dx, dy) || 1;
      const side = i % 2 === 0 ? 1 : -1;
      prints.fillEllipse(p.x + (-dy / len) * 3.5 * side, p.y + (dx / len) * 3.5 * side, 4, 6);
    }
  }

  private drawNodes(): void {
    ensurePlaceholderTexture(this, 'ui/lock');
    ensurePlaceholderTexture(this, 'ui/star_full');
    ensurePlaceholderTexture(this, 'ui/star_empty');

    for (const node of this.nodes) {
      const unlocked = this.save.isLevelUnlocked(node.level);
      const record = this.save.levelRecord(node.level);
      const radius = node.level % 10 === 0 ? NODE_RADIUS + 5 : NODE_RADIUS;
      const fill = !unlocked ? 0x3b4a63 : record.completed ? 0x2ec4b6 : 0xffc145;
      const rim = record.bestStars === 3 ? 0xffc145 : unlocked ? 0xf7fbff : 0x6b7a90;

      const disc = this.add.graphics().setDepth(2);
      disc.fillStyle(0x000000, 0.25);
      disc.fillEllipse(node.x, node.y + radius * 0.8, radius * 1.9, radius * 0.6);
      disc.fillStyle(0x1b2a41, 1);
      disc.fillCircle(node.x, node.y, radius + 5);
      disc.fillStyle(fill, 1);
      disc.fillCircle(node.x, node.y, radius);
      // A soft highlight across the top: a glossy medallion, not a flat dot.
      disc.fillStyle(0xffffff, unlocked ? 0.28 : 0.1);
      disc.fillEllipse(node.x, node.y - radius * 0.38, radius * 1.3, radius * 0.7);
      disc.lineStyle(3, rim, 1);
      disc.strokeCircle(node.x, node.y, radius);

      // Sentence levels wear a speech bubble, locked or not, so the special
      // level at the end of each world is something to look forward to.
      if (isSentenceLevel(node.level)) this.drawSentenceBadge(node, radius, unlocked);

      if (!unlocked) {
        this.add.image(node.x, node.y, 'ui/lock').setDisplaySize(24, 24).setAlpha(0.85).setDepth(3);
        continue;
      }

      this.add
        .text(node.x, node.y + 1, `${node.level}`, {
          fontFamily: FONT_FAMILY,
          fontSize: radius > NODE_RADIUS ? '26px' : '23px',
          fontStyle: 'bold',
          color: record.completed ? '#ffffff' : '#1b2a41',
          stroke: record.completed ? '#1b6f68' : '#fff3c4',
          strokeThickness: 3,
        })
        .setOrigin(0.5)
        .setDepth(3);

      // Three star slots in an arc over each finished level.
      if (record.completed) {
        for (let i = 0; i < 3; i += 1) {
          const angle = -Math.PI / 2 + (i - 1) * 0.55;
          this.add
            .image(
              node.x + Math.cos(angle) * (radius + 16),
              node.y + Math.sin(angle) * (radius + 16),
              i < record.bestStars ? 'ui/star_full' : 'ui/star_empty',
            )
            .setDisplaySize(20, 20)
            .setDepth(3);
        }
      }

      if (record.tideBestTimeSec > 0) this.drawTideBadge(node, radius, record.tideBestTimeSec);

      const hit = this.add
        .circle(node.x, node.y, radius + 8, 0xffffff, 0)
        .setDepth(4)
        .setInteractive({ useHandCursor: true });
      hit.on('pointerover', () => this.focusLevel(node.level, false));
      // A drag that happens to end on a level scrolls; it doesn't start it.
      hit.on('pointerup', () => {
        if (!this.dragged) this.play(node.level);
      });
    }
  }

  /** A speech bubble with three dots, on the upper left of a sentence level. */
  private drawSentenceBadge(node: MapNode, radius: number, unlocked: boolean): void {
    const cx = node.x - radius - 16;
    const cy = node.y - radius + 4;
    const g = this.add
      .graphics()
      .setDepth(3.1)
      .setAlpha(unlocked ? 1 : 0.65);
    g.fillStyle(0x0b1220, 0.5);
    g.fillRoundedRect(cx - 21, cy - 13, 42, 28, 11);
    g.fillStyle(0xf7fbff, 1);
    g.fillRoundedRect(cx - 19, cy - 15, 38, 26, 10);
    // The tail, pointing at the level.
    g.fillTriangle(cx + 6, cy + 9, cx + 16, cy + 9, cx + 15, cy + 18);
    g.fillStyle(0x1b2a41, 1);
    for (const dx of [-9, 0, 9]) g.fillCircle(cx + dx, cy - 2, 3);
  }

  /** Tide Run: a small wave badge under the node with the best clear time. */
  private drawTideBadge(node: MapNode, radius: number, seconds: number): void {
    const label = this.add
      .text(node.x + 9, node.y + radius + 17, `${Math.round(seconds)}s`, {
        fontFamily: FONT_FAMILY,
        fontSize: '15px',
        fontStyle: 'bold',
        color: '#ffffff',
      })
      .setOrigin(0.5)
      .setDepth(3.2);
    const width = label.width + 32;
    const left = node.x + 9 - label.width / 2 - 24;
    const top = node.y + radius + 7;
    const badge = this.add.graphics().setDepth(3.1);
    badge.fillStyle(0x0b1220, 0.6);
    badge.fillRoundedRect(left - 2, top - 2, width + 4, 24, 12);
    badge.fillStyle(0x1f78c8, 1);
    badge.fillRoundedRect(left, top, width, 20, 10);
    // Two little crests: the wave.
    badge.lineStyle(2, 0xffffff, 0.95);
    badge.beginPath();
    badge.arc(left + 9, top + 12, 4, Math.PI, 0);
    badge.arc(left + 17, top + 12, 4, Math.PI, 0);
    badge.strokePath();
  }

  /** A wooden sign at the start of each world, with its name and levels. */
  private drawSignposts(): void {
    for (let region = 0; region * 10 < this.nodes.length; region += 1) {
      const first = this.nodes[region * 10];
      const band = THEME_BANDS[region];
      if (first === undefined || band === undefined) continue;
      // Each world's first level is mid-column and the trail always leaves it
      // heading right, so the sign stands to its left, clear of the trail.
      const x = first.x - 190;
      const y = first.y + 6;
      const open = this.save.isLevelUnlocked(first.level);

      const title = this.add
        .text(x, y - 46, WORLD_NAMES[band.background], {
          fontFamily: FONT_FAMILY,
          fontSize: '19px',
          fontStyle: 'bold',
          color: '#fff8ec',
        })
        .setOrigin(0.5)
        .setDepth(3);
      const range = this.add
        .text(x, y - 24, `Levels ${band.fromLevel}-${band.toLevel}`, {
          fontFamily: FONT_FAMILY,
          fontSize: '14px',
          color: '#f4d9b8',
        })
        .setOrigin(0.5)
        .setDepth(3);
      const w = Math.max(title.width, range.width) + 28;

      const sign = this.add.graphics().setDepth(2);
      sign.fillStyle(0x000000, 0.25);
      sign.fillEllipse(x, y + 26, 60, 12);
      sign.fillStyle(0x5b3a29, 1);
      sign.fillRect(x - 4, y - 14, 8, 40);
      sign.fillStyle(0x3e2619, 1);
      sign.fillRoundedRect(x - w / 2 - 3, y - 63, w + 6, 56, 10);
      sign.fillStyle(0x8a5a3c, 1);
      sign.fillRoundedRect(x - w / 2, y - 60, w, 50, 8);
      // A cap of snow along the top of the board.
      sign.fillStyle(0xffffff, 0.95);
      sign.fillRoundedRect(x - w / 2 - 2, y - 66, w + 4, 9, 5);
      if (!open) {
        for (const object of [sign, title, range]) object.setAlpha(0.7);
      }
    }
  }

  /** The ceremonial South Pole beside the very last level. */
  private drawPole(): void {
    const tip = summitPoint(MAX_LEVEL);
    const g = this.add.graphics().setDepth(1);
    drawSouthPole(g, tip.x, tip.y + 4);
  }

  /** The player's own animal, waiting on the level they are up to. */
  private placeClimber(): void {
    const node = this.nodes[this.highest - 1];
    const profile = this.save.activeProfile();
    if (node === undefined || profile === null) return;
    const animal = animalById(profile.selectedAnimal);
    if (animal === undefined) return;
    const toward = node.x < GAME_WIDTH / 2 ? 1 : -1;
    const climber = new AnimalSprite(
      this,
      node.x + toward * 62,
      node.y + NODE_RADIUS - 2,
      animal.id,
      bodyTintFor(animal, profile.colorPerAnimal[animal.id]),
    );
    climber.setDepth(5);
    climber.setDisplayScale(0.42);
    climber.setAnimalState('idle');
    if (!this.reducedMotion()) {
      this.tweens.add({
        targets: climber,
        y: climber.y - 8,
        duration: 520,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  /** The ring that shows which level the arrow keys are on. */
  private drawFocusRing(): void {
    this.focusRing = this.add.graphics().setDepth(2.5);
    this.moveFocusRing();
    if (!this.reducedMotion()) {
      this.tweens.add({
        targets: this.focusRing,
        alpha: 0.45,
        duration: 600,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  private moveFocusRing(): void {
    const node = this.nodes[this.current - 1];
    if (this.focusRing === undefined || node === undefined) return;
    const radius = (node.level % 10 === 0 ? NODE_RADIUS + 5 : NODE_RADIUS) + 12;
    this.focusRing.clear();
    this.focusRing.lineStyle(5, 0xffc145, 1);
    this.focusRing.strokeCircle(node.x, node.y, radius);
    this.focusRing.lineStyle(2, 0xffffff, 0.9);
    this.focusRing.strokeCircle(node.x, node.y, radius + 5);
  }

  private reducedMotion(): boolean {
    return this.save.settings().reducedMotion;
  }

  private drawHeader(): void {
    const header = this.add.graphics().setScrollFactor(0).setDepth(HEADER_DEPTH);
    header.fillStyle(0x0b1220, 0.86);
    header.fillRect(0, 0, GAME_WIDTH, MAP_HEADER_HEIGHT);
    header.lineStyle(3, 0x8ecae6, 0.8);
    header.lineBetween(0, MAP_HEADER_HEIGHT, GAME_WIDTH, MAP_HEADER_HEIGHT);

    const profile = this.save.activeProfile();
    this.add
      .text(28, 22, profile?.name ?? 'Player', {
        fontFamily: FONT_FAMILY,
        fontSize: '32px',
        color: PALETTE.snow,
      })
      .setScrollFactor(0)
      .setDepth(HEADER_DEPTH + 1);

    this.starsText = this.add
      .text(28, 62, '', { fontFamily: FONT_FAMILY, fontSize: '22px', color: PALETTE.gold })
      .setScrollFactor(0)
      .setDepth(HEADER_DEPTH + 1);
    this.refreshStars();

    // In the order they read on screen, left to right, so the arrow keys move
    // the way a child expects.
    // Tide Run shows from the start, so a child knows it is coming; until
    // level 5 is done, pressing it says what opens it.
    const tideOpen = tideOpenFor(this.save);
    const buttons: ReadonlyArray<readonly [string, () => void]> = [
      ['Players', () => this.scene.start('ProfileSelect')],
      ['Settings', () => this.scene.start('Settings')],
      ['Animals', () => this.scene.start('AnimalSelect')],
      [
        'Tide Run',
        () => (tideOpen ? this.scene.start('TideStart') : this.showToast(TIDE_LOCKED_HINT)),
      ],
    ];
    buttons.forEach(([label, action], index) => {
      const button = this.add
        .text(
          IS_PORTRAIT
            ? (GAME_WIDTH / (buttons.length + 1)) * (index + 1)
            : GAME_WIDTH - 36 - (buttons.length - 1 - index) * 146,
          IS_PORTRAIT ? 126 : 48,
          label,
          {
            fontFamily: FONT_FAMILY,
            fontSize: '24px',
            color: PALETTE.snow,
            backgroundColor: '#1b2a41',
            padding: { x: 14, y: 8 },
          },
        )
        .setOrigin(IS_PORTRAIT ? 0.5 : 1, 0.5)
        .setScrollFactor(0)
        .setDepth(HEADER_DEPTH + 1)
        .setInteractive({ useHandCursor: true });
      button.on('pointerover', () => this.focusHeader(index));
      button.on('pointerout', () => {
        if (this.zone === 'header' && this.headerFocus === index) this.leaveHeader();
      });
      button.on('pointerup', () => pressButton(button, action));
      if (label === 'Tide Run' && !tideOpen) button.setAlpha(0.7);
      this.headerButtons.push(button);
      this.headerActions.push(action);
    });

    // A dark pill behind the hint, so it reads over the bright snow worlds.
    const pill = this.add.graphics().setScrollFactor(0).setDepth(HEADER_DEPTH);
    pill.fillStyle(0x0b1220, 0.72);
    const pillW = Math.min(720, GAME_WIDTH - 32);
    pill.fillRoundedRect(GAME_WIDTH / 2 - pillW / 2, GAME_HEIGHT - 46, pillW, 40, 20);
    this.add
      .text(
        GAME_WIDTH / 2,
        GAME_HEIGHT - 26,
        IS_PORTRAIT
          ? 'Tap a level to play it.'
          : 'Arrows pick a level, Enter plays.  Tab or Up from the top for the menu.',
        { fontFamily: FONT_FAMILY, fontSize: '19px', color: PALETTE.ice },
      )
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(HEADER_DEPTH + 1);
  }

  /** A short note under the header, gone after a couple of seconds. */
  private showToast(message: string): void {
    const toast = this.add
      .text(GAME_WIDTH / 2, MAP_HEADER_HEIGHT + 44, message, {
        fontFamily: FONT_FAMILY,
        fontSize: '24px',
        color: PALETTE.snow,
        backgroundColor: '#1b2a41',
        padding: { x: 18, y: 10 },
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(HEADER_DEPTH + 2);
    this.time.delayedCall(2400, () => toast.destroy());
  }

  private refreshStars(): void {
    const stars = this.save.totalStars();
    const done = this.save.levelsCompleted();
    this.starsText.setText(`${stars} stars      ${done} of ${MAX_LEVEL} levels`);
  }

  override update(): void {
    const camera = this.cameras.main;
    this.scenery?.cull(camera.scrollY, camera.scrollY + GAME_HEIGHT);
  }

  /* ----------------------------------------------------------------- input */

  private installInput(): void {
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (this.zone === 'header') {
        this.onHeaderKey(event);
        return;
      }
      if (event.key === 'Escape') {
        this.scene.start('ProfileSelect');
        return;
      }
      if (event.key === 'Tab') {
        event.preventDefault();
        this.focusHeader(0);
        return;
      }
      // Up from the highest open level has nowhere further to go on the map,
      // so it carries on up into the menu - which is where it would point.
      if (event.key === 'ArrowUp' && this.current >= this.highest) {
        event.preventDefault();
        this.focusHeader(0);
        return;
      }
      if (event.key === 'ArrowUp' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.focusLevel(Math.min(this.highest, this.current + 1), true);
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowLeft') {
        event.preventDefault();
        this.focusLevel(Math.max(1, this.current - 1), true);
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.play(this.current);
      }
    });

    this.input.on(
      'wheel',
      (_pointer: Phaser.Input.Pointer, _over: unknown, _dx: number, dy: number) => {
        this.cameras.main.scrollY += Math.sign(dy) * SCROLL_STEP;
      },
    );

    // Drag to scroll, for touch screens (and it works with a mouse too).
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.dragged = false;
      this.dragFromY = pointer.y;
      this.dragFromScroll = this.cameras.main.scrollY;
    });
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!pointer.isDown) return;
      const moved = pointer.y - this.dragFromY;
      if (Math.abs(moved) > 12) this.dragged = true;
      if (this.dragged) this.cameras.main.scrollY = this.dragFromScroll - moved;
    });
  }

  private onHeaderKey(event: KeyboardEvent): void {
    const count = this.headerButtons.length;
    if (event.key === 'ArrowLeft') {
      this.focusHeader((this.headerFocus + count - 1) % count);
    } else if (event.key === 'ArrowRight') {
      this.focusHeader((this.headerFocus + 1) % count);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const button = this.headerButtons[this.headerFocus];
      const action = this.headerActions[this.headerFocus];
      if (button !== undefined && action !== undefined) pressButton(button, action);
    } else if (event.key === 'ArrowDown' || event.key === 'Escape' || event.key === 'Tab') {
      // Back down to the levels; Esc only leaves the map from there.
      event.preventDefault();
      this.leaveHeader();
    }
  }

  private focusHeader(index: number): void {
    this.zone = 'header';
    this.headerFocus = index;
    this.headerButtons.forEach((button, i) => styleButton(button, i === index));
  }

  private leaveHeader(): void {
    this.zone = 'map';
    this.headerButtons.forEach((button) => styleButton(button, false));
  }

  private focusLevel(level: number, scroll: boolean): void {
    this.current = level;
    this.moveFocusRing();
    if (!scroll) return;
    this.tweens.add({
      targets: this.cameras.main,
      scrollY: scrollYForLevel(level, MAX_LEVEL),
      duration: 220,
      ease: 'Sine.easeOut',
    });
  }

  private play(level: number): void {
    if (!this.save.isLevelUnlocked(level)) return;
    const profile = this.save.activeProfile();
    this.scene.start('Game', {
      level,
      ...(profile === null ? {} : { animal: profile.selectedAnimal }),
    });
  }
}
